/**
 * Applications Copilot — REST helpers.
 *
 * Implements the non-streaming half of COPILOT_API_CONTRACT.md. The streaming
 * endpoint (POST /api/copilot/chat) lives in useCopilotStream.js because axios
 * cannot consume a ReadableStream.
 *
 * Every call sends the session cookie (`withCredentials`) and the global
 * apiAuthInterceptor adds the X-*-Token headers automatically.
 */
import axios from 'axios';
import { getApiUrl } from '../../../../config/api';
import { scopeParams } from './scope';

const opts = { withCredentials: true };

function qs(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const out = search.toString();
  return out ? `?${out}` : '';
}

/**
 * `scope` may be a scope object ({type:'company'} / {type:'job', job_id}) or,
 * for backwards compatibility, a bare job id. Returns `{ scope: 'company' }`
 * or `{ job_id }` for the query string.
 */
function scopeQuery(scope) {
  if (scope && typeof scope === 'object') return scopeParams(scope);
  if (scope === null || scope === undefined || scope === '') return { scope: 'company' };
  return { job_id: scope };
}

/**
 * GET /api/copilot/status?scope=company | ?job_id=<int>
 * → { enabled, voice_enabled, model, limits, actor_type, job?, scopes? }
 */
export async function fetchStatus(scope) {
  const res = await axios.get(getApiUrl(`/api/copilot/status${qs(scopeQuery(scope))}`), opts);
  return res.data || {};
}

/** GET /api/copilot/conversations?scope=company | ?job_id=<int>&limit=20 → Conversation[] */
export async function fetchConversations(scope, limit = 20) {
  const res = await axios.get(
    getApiUrl(`/api/copilot/conversations${qs({ ...scopeQuery(scope), limit })}`),
    opts
  );
  return Array.isArray(res.data?.conversations) ? res.data.conversations : [];
}

/** GET /api/copilot/conversations/<id> → { conversation, messages } */
export async function fetchConversation(id) {
  const res = await axios.get(getApiUrl(`/api/copilot/conversations/${encodeURIComponent(id)}`), opts);
  const data = res.data || {};
  return {
    conversation: data.conversation || null,
    messages: Array.isArray(data.messages) ? data.messages : [],
  };
}

/** PATCH /api/copilot/conversations/<id>  body { title?, pinned? } */
export async function patchConversation(id, body) {
  const res = await axios.patch(
    getApiUrl(`/api/copilot/conversations/${encodeURIComponent(id)}`),
    body || {},
    opts
  );
  return res.data || { ok: true };
}

/** DELETE /api/copilot/conversations/<id> (soft-archive) */
export async function deleteConversation(id) {
  const res = await axios.delete(getApiUrl(`/api/copilot/conversations/${encodeURIComponent(id)}`), opts);
  return res.data || { ok: true };
}

/** POST /api/copilot/feedback  body { message_id, rating: 1|-1, note? } */
export async function sendFeedback(messageId, rating, note) {
  const body = { message_id: messageId, rating };
  if (note) body.note = note;
  const res = await axios.post(getApiUrl('/api/copilot/feedback'), body, opts);
  return res.data || { ok: true };
}

/** GET /api/copilot/suggestions?scope=company | ?job_id=<int> → Suggestion[] ({ text, icon }) */
export async function fetchSuggestions(scope) {
  const res = await axios.get(getApiUrl(`/api/copilot/suggestions${qs(scopeQuery(scope))}`), opts);
  return Array.isArray(res.data?.suggestions) ? res.data.suggestions : [];
}

/** POST /api/copilot/chat/<stream_id>/cancel → { ok } — best effort, never throws. */
export async function cancelStream(streamId) {
  if (!streamId) return { ok: false };
  try {
    const res = await axios.post(
      getApiUrl(`/api/copilot/chat/${encodeURIComponent(streamId)}/cancel`),
      {},
      opts
    );
    return res.data || { ok: true };
  } catch {
    return { ok: false };
  }
}

/**
 * GET /api/speech/token → { token, region, expires_in }.
 * Throws the axios error on 503 (Azure speech not configured) so the voice
 * layer can fall back to the browser's built-in speech APIs.
 */
export async function fetchSpeechToken() {
  const res = await axios.get(getApiUrl('/api/speech/token'), opts);
  return res.data || {};
}

/** Normalise an axios/fetch error into { status, code, message, retryAfter, upgradeRequired }. */
export function describeApiError(err, fallback = 'Something went wrong.') {
  const status = err?.response?.status ?? err?.status ?? null;
  const data = err?.response?.data ?? err?.data ?? {};
  return {
    status,
    code: data?.code || data?.error || (status === 429 ? 'rate_limited' : status ? `http_${status}` : 'network'),
    message: data?.message || data?.error || err?.message || fallback,
    retryAfter: Number(data?.retry_after) || null,
    upgradeRequired: Boolean(data?.upgrade_required),
  };
}
